/**
 * Admin Product Fields Page
 *
 * Super-admin registry for admin-defined custom product fields. Lists every
 * definition with a clear "Applies to" scope (Trusscore default / All
 * retailers / specific retailers) and opens the edit modal for create/edit/
 * delete. Per-product VALUES are edited in the Product modal (Trusscore-default
 * fields) and the Retailer modal (per-retailer fields).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tags, Plus, Pencil, Loader2, AlertTriangle, Link2, Eye, EyeOff } from 'lucide-react';
import { AdminFieldDefEditModal } from './AdminFieldDefEditModal';
import {
  fetchFieldDefs,
  deleteFieldDef,
  type AdminFieldDef,
  type FieldDefRetailerSummary,
} from '../../services/adminFieldDefsApi';

export const AdminFieldDefs: React.FC = () => {
  const [defs, setDefs] = useState<AdminFieldDef[]>([]);
  const [retailers, setRetailers] = useState<FieldDefRetailerSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editTargetId, setEditTargetId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchFieldDefs();
      setDefs(data.fieldDefs);
      setRetailers(data.retailers);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load field definitions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const retailerName = useMemo(() => {
    const map = new Map<number, string>();
    retailers.forEach(r => map.set(r.id, r.name));
    return map;
  }, [retailers]);

  const appliesTo = (d: AdminFieldDef): React.ReactNode => {
    if (d.scope === 'trusscore') return <span className="badge text-bg-info">Trusscore default</span>;
    if (d.allRetailers) return <span className="badge text-bg-primary">All retailers</span>;
    if (d.retailerIds.length === 0) return <span className="badge text-bg-secondary">No retailers</span>;
    return (
      <span className="d-inline-flex flex-wrap gap-1">
        {d.retailerIds.map(id => (
          <span key={id} className="badge text-bg-light border">{retailerName.get(id) ?? `#${id}`}</span>
        ))}
      </span>
    );
  };

  const outputSummary = (d: AdminFieldDef): React.ReactNode => {
    if (!d.showInBom && !d.showInPlacementGuide) {
      return <span className="text-muted small d-inline-flex align-items-center gap-1"><EyeOff size={12} /> Not shown</span>;
    }
    const where = [d.showInBom && 'BOM', d.showInPlacementGuide && 'Placement'].filter(Boolean).join(' + ');
    return (
      <span className="small d-inline-flex align-items-center gap-1">
        <Eye size={12} /> {where} <span className="text-muted">· {d.outputMode}</span>
      </span>
    );
  };

  const handleDelete = async (id: number, confirmKey: string) => {
    try {
      await deleteFieldDef(id, confirmKey);
      setDefs(prev => prev.filter(d => d.id !== id));
      setEditTargetId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete field definition');
    }
  };

  return (
    <div>
      <div className="bg-white rounded-3 shadow-sm p-3 mb-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div>
            <h2 className="fw-bold mb-1 d-flex align-items-center gap-2">
              <Tags size={22} /> Product Fields
            </h2>
            <p className="text-muted small mb-0">
              Custom, typed data fields attached to products — e.g. a retailer&apos;s own SKUs. Values are
              entered in the Product modal (Trusscore-default) and the Retailer modal (per-retailer).
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
            onClick={() => setCreateOpen(true)}
          >
            <Plus size={14} /> Add Field
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning py-2 px-3 small d-flex align-items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-sm">
        <div className="table-responsive">
          <table className="table table-bordered table-striped table-hover mb-0 align-middle">
            <thead className="table-tc-slate">
              <tr>
                <th>Label</th>
                <th>Key</th>
                <th>Type</th>
                <th>Applies to</th>
                <th>Output</th>
                <th className="text-center" title="Active">Active</th>
                <th className="text-center" style={{ width: 60 }}>Edit</th>
              </tr>
            </thead>
            <tbody className="table-group-divider">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-4 text-muted"><Loader2 size={16} className="me-2" style={{ animation: 'spin 1s linear infinite' }} />Loading…</td></tr>
              ) : defs.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-4 text-muted">No custom fields defined yet.</td></tr>
              ) : defs.map(d => (
                <tr key={d.id} className={d.isActive ? '' : 'text-muted'}>
                  <td>
                    <span className="fw-semibold small d-inline-flex align-items-center gap-1">
                      {(d.urlTemplate || d.dataType === 'url') && <Link2 size={12} className="text-primary" />}
                      {d.label}
                    </span>
                    {d.columnHeading && d.columnHeading !== d.label && (
                      <div className="text-muted" style={{ fontSize: '0.7em' }}>col: {d.columnHeading}</div>
                    )}
                  </td>
                  <td className="font-monospace small">{d.fieldKey}</td>
                  <td className="small">{d.dataType}</td>
                  <td>{appliesTo(d)}</td>
                  <td>{outputSummary(d)}</td>
                  <td className="text-center">
                    <span className={`badge ${d.isActive ? 'text-bg-success' : 'text-bg-secondary'}`}>{d.isActive ? 'Yes' : 'No'}</span>
                  </td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none"
                      title="Edit field"
                      onClick={() => setEditTargetId(d.id)}
                    >
                      <Pencil size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(editTargetId !== null || createOpen) && (
        <AdminFieldDefEditModal
          fieldDefId={editTargetId}
          isCreating={createOpen}
          retailers={retailers}
          allDefs={defs}
          onClose={() => {
            setEditTargetId(null);
            setCreateOpen(false);
          }}
          onSaved={refresh}
          onDeleted={handleDelete}
        />
      )}
    </div>
  );
};

export default AdminFieldDefs;
